import type { QuickAddResult } from "@letsdoit/quick-add";
import type { Command } from "../api/types";
import { command, newUuid } from "./engine";
import type { SyncState } from "./state";

export interface QuickDefaults {
  projectId?: string | null;
  sectionId?: string | null;
  parentId?: string | null;
  /** Default due when the text has none (e.g. adding in Today / an Upcoming day). */
  due?: { date: string } | null;
  labels?: string[];
  orderKey?: string;
}

/**
 * Turn a locally parsed Quick Add result into Sync commands — like the official clients do —
 * so the task appears immediately. The server re-parses the due/reminder strings, so it stays
 * the source of truth for dates: we send `{string, lang}` and only use our parse as a preview.
 */
export function quickAddCommands(
  r: QuickAddResult,
  description: string,
  defaults: QuickDefaults,
  state: SyncState,
): { commands: Command[]; itemTempId: string } {
  const commands: Command[] = [];
  const itemTempId = `tmp-${newUuid()}`;

  for (const l of r.labels) {
    const exists = Object.values(state.labels).some((x) => x.name.toLowerCase() === l.name.toLowerCase());
    if (l.isNew && !exists) commands.push(command("label_add", { name: l.name }, `tmp-${newUuid()}`));
  }

  const projectId = r.project?.id ?? defaults.projectId ?? state.user?.inbox_project_id;
  const sectionId = r.section?.id ?? (r.project ? null : (defaults.sectionId ?? null));
  const parentId = r.project || r.section ? null : (defaults.parentId ?? null);
  const labels = [...(defaults.labels ?? [])];
  for (const l of r.labels) if (!labels.includes(l.name)) labels.push(l.name);

  const args: Record<string, unknown> = { content: r.content.trim() };
  if (description.trim()) args.description = description.trim();
  if (parentId) args.parent_id = parentId;
  else if (sectionId) args.section_id = sectionId;
  if (projectId && !parentId) args.project_id = projectId;
  if (r.priority) args.priority = r.priority;
  if (labels.length) args.labels = labels;
  if (r.assignee) args.responsible_uid = r.assignee.id;
  if (r.deadline) args.deadline = { date: r.deadline.date };
  if (r.duration) args.duration = r.duration;
  if (defaults.orderKey && !r.project && !r.section) args.order_key = defaults.orderKey;
  const preview: Record<string, unknown> = {};
  if (r.due) {
    args.due = { string: r.due.string, lang: r.due.lang };
    if (r.due.due) preview.due = r.due.due;
  } else if (defaults.due) {
    args.due = defaults.due;
  }
  const add = command("item_add", args, itemTempId);
  if (Object.keys(preview).length) add.preview = preview;
  commands.push(add);

  for (const rem of r.reminders) {
    commands.push(
      command(
        "reminder_add",
        rem.type === "relative"
          ? { item_id: itemTempId, type: "relative", minute_offset: rem.minuteOffset }
          : { item_id: itemTempId, type: "absolute", due: { string: rem.string } },
        `tmp-${newUuid()}`,
      ),
    );
  }
  return { commands, itemTempId };
}
